import SEO from "@/components/SEO";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { ArrowButton } from "@/components/ArrowButton";
import Modalidades from "@/sections/Modalidades";
import CtaBand from "@/sections/CtaBand";
import RootLine from "@/components/RootLine";
import { waTopic } from "@/data/site";

const ESPACOS = [
  {
    title: "Crianças",
    text: "A psicoterapia infantil considera o desenvolvimento, as emoções, o comportamento e as relações da criança, com participação da família ao longo do processo.",
  },
  {
    title: "Adolescentes",
    text: "Um espaço de escuta para questões emocionais, comportamentais, sociais e acadêmicas, respeitando o momento singular da adolescência.",
  },
  {
    title: "Adultos",
    text: "Acompanhamento voltado à compreensão das demandas emocionais, comportamentais e relacionais, respeitando a história de cada pessoa.",
  },
];

const Psicoterapia = () => (
  <>
    <SEO
      title="Psicoterapia para crianças, adolescentes e adultos | Sabrina Mota Psicóloga"
      description="Psicoterapia presencial e online para crianças, adolescentes e adultos. Um espaço para compreender, elaborar e construir novos caminhos — Sabrina Mota, Psicóloga CRP 06/201923."
      path="/psicoterapia"
    />
    <section className="pt-28 lg:pt-40" data-testid="psicoterapia-page">
      <div className="container-x pb-16 lg:pb-20">
        <FadeUp>
          <p className="eyebrow" data-testid="psicoterapia-eyebrow">Psicoterapia</p>
        </FadeUp>
        <FadeUp delay={0.08}>
          <h1 className="mt-5 max-w-4xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="psicoterapia-title">
            Um espaço para compreender, elaborar e construir <em className="italic text-raiz-700">novos caminhos.</em>
          </h1>
        </FadeUp>
        <FadeUp delay={0.16}>
          <p className="lead mt-8 max-w-2xl">
            A psicoterapia é um processo profissional desenvolvido a partir da escuta, da compreensão da demanda e de objetivos construídos durante o acompanhamento. Cada trajetória é singular — e é respeitada como tal.
          </p>
        </FadeUp>
        <FadeUp delay={0.24} className="mt-10">
          <ArrowButton href={waTopic("a psicoterapia")} target="_blank" rel="noopener noreferrer" testId="psicoterapia-cta-button">
            Conhecer a Psicoterapia
          </ArrowButton>
        </FadeUp>
      </div>
      <RootLine className="h-16 lg:h-24" />
    </section>

    <section className="bg-cream py-20 lg:py-28" data-testid="psicoterapia-publicos">
      <div className="container-x">
        <FadeUp>
          <h2 className="max-w-2xl font-serif text-3xl font-light leading-[1.12] tracking-tight text-ink sm:text-4xl" data-testid="psicoterapia-publicos-title">
            Para cada momento da vida
          </h2>
        </FadeUp>
        <Stagger className="mt-12 grid gap-6 md:grid-cols-3" gap={0.12}>
          {ESPACOS.map((e, i) => (
            <StaggerItem key={e.title}>
              <article
                data-testid={`psicoterapia-${e.title.toLowerCase()}`}
                className="group h-full rounded-3xl border border-raiz-900/10 bg-paper p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-raiz-600/50 lg:p-10"
              >
                <p className="font-serif text-4xl font-light text-raiz-300">0{i + 1}</p>
                <h3 className="mt-6 font-serif text-2xl font-light text-ink">{e.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-ink/65">{e.text}</p>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    <section className="py-20 lg:py-28" data-testid="psicoterapia-processo">
      <div className="container-x grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <FadeUp>
            <h2 className="font-serif text-3xl font-light leading-[1.12] tracking-tight text-ink sm:text-4xl" data-testid="psicoterapia-processo-title">
              Um processo construído com ética e ciência
            </h2>
          </FadeUp>
        </div>
        <div className="lg:col-span-7">
          <FadeUp delay={0.1} className="space-y-5 text-base leading-relaxed text-ink/70">
            <p>
              O acompanhamento psicoterápico não segue fórmulas prontas. Ele é construído encontro a encontro, a partir da escuta qualificada e dos objetivos definidos em conjunto — com responsabilidade técnica e respeito ao tempo de cada pessoa.
            </p>
            <p>
              Por compromisso ético, este trabalho não promete cura, resultados garantidos ou transformações em um número determinado de sessões. O que ele oferece é um espaço profissional, estruturado e sigiloso para compreender e elaborar aquilo que trouxe você até aqui.
            </p>
            <p className="text-sm text-ink/50">
              Prática em conformidade com o Código de Ética Profissional do Psicólogo e as resoluções do Conselho Federal de Psicologia.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>

    <Modalidades />
    <CtaBand title="Conversar sobre psicoterapia" text="Conte brevemente o que você está buscando e receba orientação sobre o primeiro passo." />
  </>
);

export default Psicoterapia;
